import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'recordButton', 'audio', 'audioLink', 'form'];
  static values = { recording: Boolean };

  connect() {
    this.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!this.SpeechRecognition) {
      console.warn('SpeechRecognition API not supported in this browser.');
      this.recordButtonTarget.disabled = true;
      this.recordButtonTarget.title = 'Voice input is not supported in this browser.';
      this.showUnsupportedBrowserWarning();
      return;
    }

    this.recognition = new this.SpeechRecognition();
    this.recognition.lang = this.element.dataset.lang || 'en-US';
    this.recognition.interimResults = false;
    this.recognition.maxAlternatives = 1;

    this.recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      transcript = this.cleanAndCapitalizeName(transcript);
      this.inputTarget.value = transcript;
    };

    this.recognition.onend = () => {
      this.recordingValue = false;
      this.updateButtonState();
      if (this.inputTarget.value.trim()) {
        this.formTarget.submit();
      }
    };

    this.recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      this.recordingValue = false;
      this.updateButtonState();
      alert(`Error with voice input: ${event.error}. Please try again or type your name.`);
    };

    this.audioTarget.addEventListener('ended', () => {
      if (!this.recordingValue) {
        this.startRecording();
      }
    });
  }

  toggleRecording(event) {
    event.preventDefault();
    if (!this.recordingValue) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  playAudioAndPrepareRecording(event) {
    event.preventDefault();
    this.audioTarget.play().catch((error) => {
      console.error('Error playing audio:', error);
      alert('Unable to play audio. Please try again or type your name.');
    });
  }

  startRecording() {
    try {
      this.recognition.start();
      this.recordingValue = true;
      this.updateButtonState();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      alert('Unable to start voice input. Please try again or type your name.');
    }
  }

  stopRecording() {
    this.recognition.stop();
  }

  recordingValueChanged() {
    this.updateButtonState();
  }

  updateButtonState() {
    const button = this.recordButtonTarget;
    if (this.recordingValue) {
      button.classList.add('btn-danger', 'recording');
      button.classList.remove('btn-outline-primary');
      button.innerHTML = '<i class="bi bi-mic-mute-fill"></i> Stop Recording';
      button.setAttribute('aria-pressed', 'true');
    } else {
      button.classList.add('btn-outline-primary');
      button.classList.remove('btn-danger', 'recording');
      button.innerHTML = '<i class="bi bi-mic-fill"></i> Record Name';
      button.setAttribute('aria-pressed', 'false');
    }
  }

  showUnsupportedBrowserWarning() {
    const warning = document.createElement('div');
    warning.className = 'alert alert-warning mt-2';
    warning.textContent = 'Voice input is not supported in your browser. Please type your name.';
    this.element.appendChild(warning);
  }

  cleanAndCapitalizeName(transcript) {
    const phrasesToRemove = ['my name is', 'this is', 'i am', "I'm", "my name's", 'name is'];
    let cleaned = transcript.toLowerCase();
    phrasesToRemove.forEach((phrase) => {
      cleaned = cleaned.replace(new RegExp(`\\b${phrase}\\b`, 'gi'), '').trim();
    });

    return cleaned
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
